import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { siteBuilderAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  FiSave, FiEye, FiBell, FiMessageSquare, FiStar, FiPhone,
  FiExternalLink, FiCopy, FiCheck, FiSettings, FiGift, 
  FiMail, FiInstagram, FiFacebook, FiMapPin, FiGlobe, FiCalendar,
  FiYoutube
} from 'react-icons/fi';
import { FaTiktok, FaSnapchatGhost } from 'react-icons/fa';

export default function SiteSettingsPage() {
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Site settings state
  const [siteSettings, setSiteSettings] = useState({
    top_banner: {
      enabled: false,
      text: '',
      promo_code: '',
      background_color: '#d4af37',
      text_color: '#000000',
      link: '',
      closable: true
    },
    floating_buttons: {
      call_enabled: true,
      quote_enabled: true,
      booking_enabled: true
    },
    booking: {
      deposit_percentage: 30,
      free_quote_text: 'Demander un devis gratuit',
      apply_promo_at_checkout: true
    },
    blog_enabled: false  // Toggle pour afficher/masquer le blog dans le menu
  });

  // Contact & Social info (stored in footer_content)
  const [contactInfo, setContactInfo] = useState({
    phone: '',
    email: '',
    address: '',
    hours: '',
    footer_description: '',
    socialLinks: {
      instagram: '',
      facebook: '',
      tiktok: '',
      youtube: '',
      snapchat: ''
    }
  });

  useEffect(() => {
    loadSite();
  }, []);

  const loadSite = async () => {
    try {
      const response = await siteBuilderAPI.getMySite();
      setSite(response.data);
      if (response.data.site_settings) {
        setSiteSettings(prev => ({
          ...prev,
          ...response.data.site_settings,
          top_banner: { ...prev.top_banner, ...response.data.site_settings.top_banner },
          floating_buttons: { ...prev.floating_buttons, ...response.data.site_settings.floating_buttons },
          booking: { ...prev.booking, ...response.data.site_settings.booking }
        }));
      }
      // Load contact info from footer_content
      if (response.data.footer_content) {
        setContactInfo(prev => ({
          ...prev,
          phone: response.data.footer_content.phone || response.data.contact_info?.phone || '',
          email: response.data.footer_content.email || response.data.contact_info?.email || '',
          address: response.data.footer_content.address || '',
          hours: response.data.contact_info?.hours || '',
          footer_description: response.data.footer_content.description || '',
          socialLinks: {
            ...prev.socialLinks,
            ...response.data.footer_content.socialLinks
          }
        }));
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await siteBuilderAPI.updateSite({
        ...site,
        site_settings: siteSettings,
        footer_content: {
          ...site.footer_content,
          phone: contactInfo.phone,
          email: contactInfo.email,
          address: contactInfo.address,
          description: contactInfo.footer_description,
          socialLinks: contactInfo.socialLinks
        },
        contact_info: {
          ...site.contact_info,
          phone: contactInfo.phone,
          email: contactInfo.email,
          hours: contactInfo.hours,
          instagram: contactInfo.socialLinks.instagram,
          facebook: contactInfo.socialLinks.facebook
        }
      });
      toast.success('Paramètres enregistrés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateTopBanner = (field, value) => {
    setSiteSettings(prev => ({
      ...prev,
      top_banner: { ...prev.top_banner, [field]: value }
    }));
  };

  const updateFloatingButtons = (field, value) => {
    setSiteSettings(prev => ({
      ...prev,
      floating_buttons: { ...prev.floating_buttons, [field]: value }
    }));
  };

  const updateBooking = (field, value) => {
    setSiteSettings(prev => ({
      ...prev,
      booking: { ...prev.booking, [field]: value }
    }));
  };

  const updateContact = (field, value) => {
    setContactInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateSocialLink = (platform, value) => {
    setContactInfo(prev => ({
      ...prev,
      socialLinks: { ...prev.socialLinks, [platform]: value }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-2 border-violet-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Paramètres du Site</h1>
          <p className="text-slate-500 mt-1">Gérez le bandeau d'annonces, les boutons flottants et les options de réservation</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => window.open(`/preview/${site?.site_id}`, '_blank')}>
            <FiEye className="mr-2 h-4 w-4" /> Prévisualiser
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
            ) : (
              <FiSave className="mr-2 h-4 w-4" />
            )}
            Enregistrer
          </Button>
        </div>
      </div>

      <Tabs defaultValue="banner" className="space-y-6">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="banner" className="flex items-center gap-2">
            <FiBell className="h-4 w-4" /> Bandeau d'Annonces
          </TabsTrigger>
          <TabsTrigger value="contact" className="flex items-center gap-2">
            <FiPhone className="h-4 w-4" /> Contact & Réseaux
          </TabsTrigger>
          <TabsTrigger value="floating" className="flex items-center gap-2">
            <FiMessageSquare className="h-4 w-4" /> Boutons Flottants
          </TabsTrigger>
          <TabsTrigger value="blog" className="flex items-center gap-2">
            <FiGlobe className="h-4 w-4" /> Blog
          </TabsTrigger>
        </TabsList>

        {/* Top Banner Tab */}
        <TabsContent value="banner" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FiBell className="h-5 w-5 text-yellow-500" />
                Bandeau d'Annonces
              </CardTitle>
              <CardDescription>
                Affichez un bandeau en haut de votre site pour promouvoir des offres, annoncer des fermetures, etc.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div>
                  <Label className="text-base font-medium">Activer le bandeau</Label>
                  <p className="text-sm text-slate-500">Afficher le bandeau d'annonces en haut du site</p>
                </div>
                <Switch 
                  checked={siteSettings.top_banner.enabled}
                  onCheckedChange={(v) => updateTopBanner('enabled', v)}
                />
              </div>

              {siteSettings.top_banner.enabled && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-4"
                >
                  <div>
                    <Label>Texte du bandeau</Label>
                    <Textarea 
                      value={siteSettings.top_banner.text}
                      onChange={(e) => updateTopBanner('text', e.target.value)}
                      placeholder="🎉 OFFRE SPÉCIALE : -10% sur toutes nos prestations !"
                      rows={2}
                      className="mt-1"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="flex items-center gap-2">
                        <FiGift className="h-4 w-4" /> Code Promo (optionnel)
                      </Label>
                      <Input 
                        value={siteSettings.top_banner.promo_code}
                        onChange={(e) => updateTopBanner('promo_code', e.target.value.toUpperCase())}
                        placeholder="SMILE2025"
                        className="mt-1 font-mono"
                      />
                      <p className="text-xs text-slate-500 mt-1">Les visiteurs pourront copier ce code</p>
                    </div>
                    <div>
                      <Label>Lien (optionnel)</Label>
                      <Input 
                        value={siteSettings.top_banner.link}
                        onChange={(e) => updateTopBanner('link', e.target.value)}
                        placeholder="https://..."
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Couleur de fond</Label>
                      <div className="flex gap-2 mt-1">
                        <input 
                          type="color"
                          value={siteSettings.top_banner.background_color}
                          onChange={(e) => updateTopBanner('background_color', e.target.value)}
                          className="w-12 h-10 rounded border cursor-pointer"
                        />
                        <Input 
                          value={siteSettings.top_banner.background_color}
                          onChange={(e) => updateTopBanner('background_color', e.target.value)}
                          className="flex-1 font-mono"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Couleur du texte</Label>
                      <div className="flex gap-2 mt-1">
                        <input 
                          type="color"
                          value={siteSettings.top_banner.text_color}
                          onChange={(e) => updateTopBanner('text_color', e.target.value)}
                          className="w-12 h-10 rounded border cursor-pointer"
                        />
                        <Input 
                          value={siteSettings.top_banner.text_color}
                          onChange={(e) => updateTopBanner('text_color', e.target.value)}
                          className="flex-1 font-mono"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Switch 
                      checked={siteSettings.top_banner.closable}
                      onCheckedChange={(v) => updateTopBanner('closable', v)}
                    />
                    <Label>Permettre aux visiteurs de fermer le bandeau</Label>
                  </div>

                  {/* Preview */}
                  <div className="mt-6">
                    <Label className="text-sm font-medium mb-2 block">Aperçu</Label>
                    <div 
                      className="py-2 px-4 text-center text-sm font-medium rounded-lg"
                      style={{ 
                        backgroundColor: siteSettings.top_banner.background_color,
                        color: siteSettings.top_banner.text_color
                      }}
                    >
                      <div className="flex items-center justify-center gap-3 flex-wrap">
                        <span>{siteSettings.top_banner.text || 'Votre message ici...'}</span>
                        {siteSettings.top_banner.promo_code && (
                          <span className="px-3 py-1 rounded-full bg-black/20 font-bold">
                            {siteSettings.top_banner.promo_code}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact & Social Tab */}
        <TabsContent value="contact" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FiPhone className="h-5 w-5 text-green-500" />
                Informations de Contact
              </CardTitle>
              <CardDescription>
                Ces informations seront affichées dans le header et le footer de votre site
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <FiPhone className="h-4 w-4" /> Téléphone
                  </Label>
                  <Input 
                    value={contactInfo.phone}
                    onChange={(e) => updateContact('phone', e.target.value)}
                    placeholder="06 12 34 56 78"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <FiMail className="h-4 w-4" /> Email
                  </Label>
                  <Input 
                    type="email"
                    value={contactInfo.email}
                    onChange={(e) => updateContact('email', e.target.value)}
                    placeholder="contact@votre-site.com"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <FiMapPin className="h-4 w-4" /> Adresse
                </Label>
                <Input 
                  value={contactInfo.address}
                  onChange={(e) => updateContact('address', e.target.value)}
                  placeholder="23 Av. de la Crau, 13118 Istres"
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  🕐 Horaires d'ouverture
                </Label>
                <Input 
                  value={contactInfo.hours}
                  onChange={(e) => updateContact('hours', e.target.value)}
                  placeholder="Lun-Sam : 9h-19h"
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  📝 Description du Footer
                </Label>
                <p className="text-xs text-slate-500 mb-1">Ce texte apparaît sous le logo dans le footer du site</p>
                <Textarea 
                  value={contactInfo.footer_description}
                  onChange={(e) => updateContact('footer_description', e.target.value)}
                  placeholder="Spécialiste de la location de PhotoBooth, VideoBooth 360 et Livre d'Or Vidéo pour tous vos événements."
                  className="mt-1"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FiGlobe className="h-5 w-5 text-blue-500" />
                Réseaux Sociaux
              </CardTitle>
              <CardDescription>
                Ajoutez les liens vers vos profils sur les réseaux sociaux
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <FiInstagram className="h-4 w-4 text-pink-500" /> Instagram
                  </Label>
                  <Input 
                    value={contactInfo.socialLinks.instagram}
                    onChange={(e) => updateSocialLink('instagram', e.target.value)}
                    placeholder="https://instagram.com/votre-compte"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <FiFacebook className="h-4 w-4 text-blue-600" /> Facebook
                  </Label>
                  <Input 
                    value={contactInfo.socialLinks.facebook}
                    onChange={(e) => updateSocialLink('facebook', e.target.value)}
                    placeholder="https://facebook.com/votre-page"
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    🎵 TikTok
                  </Label>
                  <Input 
                    value={contactInfo.socialLinks.tiktok}
                    onChange={(e) => updateSocialLink('tiktok', e.target.value)}
                    placeholder="https://tiktok.com/@votre-compte"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    ▶️ YouTube
                  </Label>
                  <Input 
                    value={contactInfo.socialLinks.youtube}
                    onChange={(e) => updateSocialLink('youtube', e.target.value)}
                    placeholder="https://youtube.com/@votre-chaine"
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    👻 Snapchat
                  </Label>
                  <Input 
                    value={contactInfo.socialLinks.snapchat}
                    onChange={(e) => updateSocialLink('snapchat', e.target.value)}
                    placeholder="https://snapchat.com/add/votre-compte"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Preview */}
              <div className="mt-6 p-4 bg-slate-900 rounded-xl">
                <Label className="text-white text-sm font-medium mb-3 block">Aperçu des icônes</Label>
                <div className="flex gap-3 flex-wrap">
                  {contactInfo.email && (
                    <a href={`mailto:${contactInfo.email}`} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors">
                      <FiMail className="h-5 w-5" />
                    </a>
                  )}
                  {contactInfo.socialLinks.instagram && (
                    <a href={contactInfo.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors">
                      <FiInstagram className="h-5 w-5" />
                    </a>
                  )}
                  {contactInfo.socialLinks.facebook && (
                    <a href={contactInfo.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors">
                      <FiFacebook className="h-5 w-5" />
                    </a>
                  )}
                  {contactInfo.socialLinks.tiktok && (
                    <a href={contactInfo.socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors">
                      <FaTiktok className="h-5 w-5" />
                    </a>
                  )}
                  {contactInfo.socialLinks.youtube && (
                    <a href={contactInfo.socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors">
                      <FiYoutube className="h-5 w-5" />
                    </a>
                  )}
                  {contactInfo.socialLinks.snapchat && (
                    <a href={contactInfo.socialLinks.snapchat} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors">
                      <FaSnapchatGhost className="h-5 w-5" />
                    </a>
                  )}
                  {!contactInfo.email && !contactInfo.socialLinks.instagram && !contactInfo.socialLinks.facebook && !contactInfo.socialLinks.tiktok && !contactInfo.socialLinks.youtube && !contactInfo.socialLinks.snapchat && (
                    <p className="text-white/50 text-sm">Aucun réseau social configuré</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Floating Buttons Tab */}
        <TabsContent value="floating" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FiMessageSquare className="h-5 w-5 text-blue-500" />
                Menu Actions Rapides
              </CardTitle>
              <CardDescription>
                Configurez les boutons d'action rapide affichés dans le menu flottant en bas à droite de votre site
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Call Button */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <FiPhone className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <Label className="text-base font-medium">Appeler</Label>
                    <p className="text-sm text-slate-500">Lance un appel téléphonique direct vers votre numéro</p>
                  </div>
                </div>
                <Switch 
                  checked={siteSettings.floating_buttons.call_enabled}
                  onCheckedChange={(v) => updateFloatingButtons('call_enabled', v)}
                />
              </div>

              {/* Quote Button */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <FiMessageSquare className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <Label className="text-base font-medium">Demande de Devis</Label>
                    <p className="text-sm text-slate-500">Fait défiler vers la section contact pour une demande de devis</p>
                  </div>
                </div>
                <Switch 
                  checked={siteSettings.floating_buttons.quote_enabled}
                  onCheckedChange={(v) => updateFloatingButtons('quote_enabled', v)}
                />
              </div>

              {/* Booking Button */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <FiCalendar className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <Label className="text-base font-medium">Réserver en ligne</Label>
                    <p className="text-sm text-slate-500">Ouvre le formulaire de réservation en ligne</p>
                  </div>
                </div>
                <Switch 
                  checked={siteSettings.floating_buttons.booking_enabled}
                  onCheckedChange={(v) => updateFloatingButtons('booking_enabled', v)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Blog Tab */}
        <TabsContent value="blog" className="space-y-6">
          {/* Blog Settings Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FiGlobe className="h-5 w-5 text-purple-600" />
                Paramètres du Blog
              </CardTitle>
              <CardDescription>
                Activez ou désactivez l'affichage du blog sur votre site
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <FiMessageSquare className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <Label className="text-base font-medium">Activer le Blog</Label>
                    <p className="text-sm text-slate-500">Affiche l'onglet Blog dans le menu et la page du blog</p>
                  </div>
                </div>
                <Switch 
                  checked={siteSettings.blog_enabled}
                  onCheckedChange={(v) => setSiteSettings(prev => ({ ...prev, blog_enabled: v }))}
                />
              </div>
              {!siteSettings.blog_enabled && (
                <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                  ⚠️ Le blog est actuellement désactivé. Les visiteurs ne verront pas l'onglet Blog dans le menu.
                </p>
              )}
              {siteSettings.blog_enabled && (
                <p className="text-sm text-green-600 bg-green-50 p-3 rounded-lg">
                  ✓ Le blog est activé. Les visiteurs peuvent accéder au blog depuis le menu du site.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
